import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { Header } from '@/components/Header.tsx'
import { LoginScope } from '@/consts/team'
import { InstanceContextProvider } from '@/contexts/InstanceContext'
import { TeamAppContext, TeamApplication } from '@/contexts/TeamContext'
import { UnifiedContextProvider } from '@/contexts/UnifiedContext'
import { useAppContext } from '@/hooks/useAppContext'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { InitPasswordModal } from '@/pages/teams/components/InitPasswordModal'
import { useTeamStore } from '@/stores/team'
import { useIsLoggedIn, useSuggestion, useUserStore } from '@/stores/user'

import { TeamMenu } from './TeamMenu'

export function TeamLayout() {
  const isLoggedIn = useIsLoggedIn()
  const { hasValidTeamToken, isTeamOnlyUser } = useAppContext()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const userStore = useUserStore()
  const teamStore = useTeamStore()
  const suggestion = useSuggestion()

  // Sync context when entering team routes via browser forward button
  useEffect(() => {
    const state = useTeamStore.getState()

    // If we have a valid team token but scopeInfo is stale (shows platform),
    // re-fetch user info to sync team context
    if (state.teamTokenData && state.scopeInfo?.scope !== LoginScope.Team) {
      userStore.getUserInfo()
    }
  }, [])

  // Guard: Redirect if no valid team token
  if (!hasValidTeamToken) {
    return <Navigate to={isTeamOnlyUser ? '/team/login' : '/create'} replace />
  }

  // Guard: Redirect if not logged in (valid team token is sufficient authentication)
  if (!isLoggedIn && !hasValidTeamToken) {
    return <Navigate to="/login" replace />
  }

  // Guard: force direct team-login users to change password before any team page
  // is accessible. Platform users switching into a team keep platform access and
  // should not be blocked by a team member password-update suggestion.
  if (isTeamOnlyUser && suggestion.needUpdatePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[image:var(--image-app-background)] bg-cover bg-center bg-no-repeat">
        <InitPasswordModal onSuccess={() => {}} />
      </div>
    )
  }

  const app: TeamApplication = {
    refresh: () => {
      // Team-specific refresh logic can be added here if needed
    },
  }

  return (
    <TeamAppContext.Provider value={app}>
      <UnifiedContextProvider
        mode="team"
        teamResID={teamStore.teamTokenData?.teamResID}
        memberResID={teamStore.scopeInfo?.resID}
      >
        <InstanceContextProvider
          mode="team"
          teamResID={teamStore.teamTokenData?.teamResID}
          memberResID={teamStore.scopeInfo?.resID}
        >
          <div className="h-full flex flex-col overflow-hidden bg-[image:var(--image-app-background)] bg-cover bg-center bg-no-repeat">
            <Header setExchargeModalOpen={setExchargeModalOpen} />
            <main className="w-full flex-1 bg-cover bg-no-repeat bg-center pb-4 flex items-start overflow-auto">
              <div className="min-w-[1400px] w-full flex gap-4 px-4 h-full">
                <TeamMenu />
                <div className="flex-1 overflow-y-auto rounded-[32px] relative">
                  <Outlet />
                </div>
              </div>
            </main>
          </div>
          {exchargeModalOpen && (
            <ExchargeModal
              onOk={() => {
                setExchargeModalOpen(false)
                userStore.getUserInfo()
              }}
              onClose={() => {
                setExchargeModalOpen(false)
              }}
            />
          )}
        </InstanceContextProvider>
      </UnifiedContextProvider>
    </TeamAppContext.Provider>
  )
}
